import BreadCrumb from 'Common/BreadCrumb';
import { AnimatedListSkeleton, AnimatedTable, AnimatedTableBody, AnimatedTableRow } from 'components/animations';
import Pagination from 'Common/Pagination';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import { IUser } from '@core/models/user-listing.model';
import { getAllUsersThunk } from '@redux/features/user-listing.slice';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { ChevronRight, Printer, RefreshCw, Settings2 } from 'lucide-react';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import defaultPosLogo from 'assets/images/finalLogo.svg';
import posService, {
    PosSettingsDto,
    PosSettingsUpdateRequest,
    PrintJobDto,
} from 'services/api/pos.service';

const PAGE_SIZE = 10;

const formatDt = (v?: string) => {
    if (!v) return '—';
    try {
        return new Date(v).toLocaleString();
    } catch {
        return v;
    }
};

/** Normalize paginated users payload from API / redux (handles content or usersWithPage). */
function getUsersPagePayload(raw: unknown): {
    content: IUser[];
    totalElements: number;
    pageSize: number;
    numberOfElements: number;
} {
    const u = raw as Record<string, unknown> | null | undefined;
    if (!u) {
        return { content: [], totalElements: 0, pageSize: PAGE_SIZE, numberOfElements: 0 };
    }
    const inner = (u.usersWithPage as Record<string, unknown>) || u;
    const content = (inner.content as IUser[]) || [];
    const totalElements = Number(inner.totalElements ?? u.totalElements ?? 0);
    const pageable = (inner.pageable as { pageSize?: number }) || (u.pageable as { pageSize?: number });
    const pageSize = Number(pageable?.pageSize ?? PAGE_SIZE);
    const numberOfElements = Number(inner.numberOfElements ?? content.length ?? 0);
    return { content, totalElements, pageSize, numberOfElements };
}

const PosSettings: FC = () => {
    const dispatch = useAppDispatch();
    const usersRaw = useAppSelector((state: RootState) => state.userListingState.users);
    const usersLoading = useAppSelector((state: RootState) => state.userListingState.loading);

    const { content: userRows, totalElements, pageSize, numberOfElements } = useMemo(
        () => getUsersPagePayload(usersRaw),
        [usersRaw]
    );

    const [listPage, setListPage] = useState(1);
    const [posMap, setPosMap] = useState<Record<number, PosSettingsDto | undefined>>({});
    const [posListLoading, setPosListLoading] = useState(false);

    const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
    const [accountIdInput, setAccountIdInput] = useState<string>('');
    const [activeAccountId, setActiveAccountId] = useState<number | null>(null);
    const [editorLoading, setEditorLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);

    const [form, setForm] = useState<PosSettingsUpdateRequest>({
        posEnabled: false,
        printerType: 'SUNMI_V2',
        storeName: '',
        storeType: '',
        headerLogoUrl: '',
        footerText: '',
        paperWidth: '58mm',
        autoPrintIfSubmit: true,
    });
    const [audit, setAudit] = useState<Pick<PosSettingsDto, 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'>>(
        {}
    );

    const [orderIdInput, setOrderIdInput] = useState<string>('');
    const [jobsLoading, setJobsLoading] = useState(false);
    const [printJobs, setPrintJobs] = useState<PrintJobDto[]>([]);

    const logoPreviewSrc =
        form.headerLogoUrl && form.headerLogoUrl.trim().length > 0 ? form.headerLogoUrl.trim() : defaultPosLogo;

    const fetchUserList = useCallback(() => {
        dispatch(
            getAllUsersThunk({
                pageNumber: String(listPage - 1),
                pageSize: String(PAGE_SIZE),
                userId: '',
                tier: '',
                accountType: '',
                email: '',
            })
        );
    }, [dispatch, listPage]);

    useEffect(() => {
        fetchUserList();
    }, [fetchUserList]);

    useEffect(() => {
        if (!userRows.length) {
            setPosMap({});
            return;
        }
        let cancelled = false;
        (async () => {
            setPosListLoading(true);
            const next: Record<number, PosSettingsDto | undefined> = {};
            await Promise.all(
                userRows.map(async (row) => {
                    const aid = row.account?.accountId;
                    if (aid == null) return;
                    try {
                        const res = await posService.getSettings(aid);
                        if (!cancelled) next[aid] = res.data as PosSettingsDto;
                    } catch {
                        if (!cancelled) next[aid] = undefined;
                    }
                })
            );
            if (!cancelled) {
                setPosMap(next);
                setPosListLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [userRows]);

    const applySettingsToForm = (dto: PosSettingsDto) => {
        setForm({
            posEnabled: dto.posEnabled ?? false,
            printerType: dto.printerType ?? 'SUNMI_V2',
            storeName: dto.storeName ?? '',
            storeType: dto.storeType ?? '',
            headerLogoUrl: dto.headerLogoUrl ?? '',
            footerText: dto.footerText ?? '',
            paperWidth: dto.paperWidth ?? '58mm',
            autoPrintIfSubmit: dto.autoPrintIfSubmit ?? true,
        });
        setAudit({
            createdAt: dto.createdAt,
            updatedAt: dto.updatedAt,
            createdBy: dto.createdBy,
            updatedBy: dto.updatedBy,
        });
    };

    const loadSettingsForAccount = useCallback(async (accountId: number, silent?: boolean) => {
        setEditorLoading(true);
        try {
            const res = await posService.getSettings(accountId);
            const dto = res.data as PosSettingsDto;
            setActiveAccountId(accountId);
            setAccountIdInput(String(accountId));
            applySettingsToForm(dto);
            setPosMap((prev) => ({ ...prev, [accountId]: dto }));
            if (!silent) toast.success('POS settings loaded', { autoClose: 2000 });
        } catch (err: any) {
            const msg =
                err?.errorMessage ||
                err?.response?.data?.message ||
                err?.message ||
                'Failed to load POS settings';
            toast.error(typeof msg === 'string' ? msg : 'Failed to load POS settings', { autoClose: 3000 });
        } finally {
            setEditorLoading(false);
        }
    }, []);

    const openEditor = (accountId: number) => {
        setSelectedAccountId(accountId);
        void loadSettingsForAccount(accountId);
    };

    const saveSettings = async () => {
        const id = activeAccountId ?? selectedAccountId ?? Number(accountIdInput);
        if (!id || Number.isNaN(id)) {
            toast.error('Select an account from the table first', { autoClose: 2500 });
            return;
        }
        setSaveLoading(true);
        try {
            const res = await posService.updateSettings(id, form);
            const dto = res.data as PosSettingsDto;
            setActiveAccountId(id);
            applySettingsToForm(dto);
            setPosMap((prev) => ({ ...prev, [id]: dto }));
            toast.success('POS settings saved', { autoClose: 2000 });
        } catch (err: any) {
            const msg =
                err?.errorMessage ||
                err?.response?.data?.message ||
                err?.message ||
                'Failed to save POS settings';
            toast.error(typeof msg === 'string' ? msg : 'Failed to save POS settings', { autoClose: 3000 });
        } finally {
            setSaveLoading(false);
        }
    };

    const loadPrintJobs = async () => {
        const oid = Number(orderIdInput);
        if (!oid || Number.isNaN(oid)) {
            toast.error('Enter a valid order ID', { autoClose: 2500 });
            return;
        }
        setJobsLoading(true);
        try {
            const res = await posService.getPrintJobs(oid);
            setPrintJobs((res.data as PrintJobDto[]) || []);
            toast.success('Print jobs loaded', { autoClose: 2000 });
        } catch (err: any) {
            const msg =
                err?.errorMessage ||
                err?.response?.data?.message ||
                err?.message ||
                'Failed to load print jobs';
            toast.error(typeof msg === 'string' ? msg : 'Failed to load print jobs', { autoClose: 3000 });
            setPrintJobs([]);
        } finally {
            setJobsLoading(false);
        }
    };

    const changeListPage = (p: string) => {
        setListPage(Number(p));
    };

    return (
        <>
            <BreadCrumb pageTitle="POS / Receipt" />
            <section className="py-5">
                <div className="px-3">
                    <div className="mb-6 flex flex-col gap-3 rounded-xl border border-slate-200/80 bg-gradient-to-r from-slate-50 to-white p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-start gap-4">
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#bd902e]/15 text-[#bd902e]">
                                <Printer className="size-6" aria-hidden />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-slate-800">POS & receipt settings</h1>
                                <p className="mt-1 max-w-2xl text-sm text-slate-600">
                                    See which accounts have POS enabled, open the editor for one account, and inspect
                                    print jobs. Receipts use the Antar logo by default when the logo URL is left empty.
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            title="Refresh account list"
                            aria-label="Refresh account list"
                            onClick={() => fetchUserList()}
                            disabled={usersLoading}
                            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
                        >
                            <RefreshCw className={`size-5 ${usersLoading ? 'animate-spin' : ''}`} aria-hidden />
                        </button>
                    </div>

                    <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
                        <div className="xl:col-span-2 space-y-6">
                            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 bg-slate-50/80 px-4 py-3">
                                    <div className="flex items-center gap-2">
                                        <Settings2 className="size-5 text-[#bd902e]" aria-hidden />
                                        <h2 className="text-base font-bold text-slate-800">Accounts & POS status</h2>
                                    </div>
                                    {posListLoading && (
                                        <span className="text-xs font-medium text-slate-500">Syncing POS flags…</span>
                                    )}
                                </div>
                                <div className="overflow-x-auto">
                                    {usersLoading ? (
                                        <div className="flex justify-center py-16">
                                            <div className="size-10 animate-spin rounded-full border-4 border-custom-500 border-l-transparent" />
                                        </div>
                                    ) : (
                                        <AnimatedTable className="table-custom w-full border-separate border-spacing-y-0 whitespace-nowrap text-sm">
                                            <thead>
                                                <tr className="bg-slate-100/90 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                                                    <th className="px-4 py-3">Account</th>
                                                    <th className="px-4 py-3">Business</th>
                                                    <th className="px-4 py-3">Type</th>
                                                    <th className="px-4 py-3">POS</th>
                                                    <th className="px-4 py-3">Auto print</th>
                                                    <th className="px-4 py-3">Updated</th>
                                                    <th className="px-4 py-3 text-center">Action</th>
                                                </tr>
                                            </thead>
                                            <AnimatedTableBody loading={usersLoading} itemCount={userRows.length} animationKey={`pos-users-${listPage}`}>
                                                {userRows.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                                                            No accounts on this page.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    userRows.map((user) => {
                                                        const aid = user.account?.accountId;
                                                        const pos = aid != null ? posMap[aid] : undefined;
                                                        const selected = aid != null && selectedAccountId === aid;
                                                        return (
                                                            <AnimatedTableRow
                                                                rowKey={user.id}
                                                                detailRecord={user}
                                                                className={`border-b border-slate-100 transition-colors ${
                                                                    selected ? 'bg-[#bd902e]/10' : 'bg-white hover:bg-slate-50/80'
                                                                }`}
                                                            >
                                                                <td className="px-4 py-3 font-mono text-slate-800">
                                                                    {aid ?? '—'}
                                                                    <div className="max-w-[200px] truncate text-xs font-normal text-slate-500">
                                                                        {user.email}
                                                                    </div>
                                                                </td>
                                                                <td className="px-4 py-3 text-slate-700">
                                                                    {user.account?.businessName || '—'}
                                                                </td>
                                                                <td className="px-4 py-3 text-slate-600">
                                                                    {user.account?.accountType || '—'}
                                                                </td>
                                                                <td className="px-4 py-3">
                                                                    {posListLoading || aid == null ? (
                                                                        <span className="text-slate-400">…</span>
                                                                    ) : pos ? (
                                                                        <span
                                                                            className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                                                                                pos.posEnabled
                                                                                    ? 'bg-emerald-100 text-emerald-800'
                                                                                    : 'bg-slate-200 text-slate-700'
                                                                            }`}
                                                                        >
                                                                            {pos.posEnabled ? 'On' : 'Off'}
                                                                        </span>
                                                                    ) : (
                                                                        <span className="text-xs text-amber-700" title="Could not load POS">
                                                                            —
                                                                        </span>
                                                                    )}
                                                                </td>
                                                                <td className="px-4 py-3">
                                                                    {posListLoading || aid == null ? (
                                                                        <span className="text-slate-400">…</span>
                                                                    ) : pos ? (
                                                                        <span className="text-slate-700">
                                                                            {pos.autoPrintIfSubmit ? 'Yes' : 'No'}
                                                                        </span>
                                                                    ) : (
                                                                        '—'
                                                                    )}
                                                                </td>
                                                                <td className="px-4 py-3 text-xs text-slate-600">
                                                                    {pos?.updatedAt ? formatDt(pos.updatedAt) : '—'}
                                                                </td>
                                                                <td className="px-4 py-3 text-center">
                                                                    {aid != null ? (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => openEditor(aid)}
                                                                            className="inline-flex items-center gap-1 rounded-lg border border-[#bd902e] bg-white px-3 py-1.5 text-xs font-semibold text-[#bd902e] transition hover:bg-[#bd902e]/10"
                                                                        >
                                                                            Configure
                                                                            <ChevronRight className="size-3.5" aria-hidden />
                                                                        </button>
                                                                    ) : (
                                                                        '—'
                                                                    )}
                                                                </td>
                                                            </AnimatedTableRow>
                                                        );
                                                    })
                                                )}
                                            </AnimatedTableBody>
                                        </AnimatedTable>
                                    )}
                                </div>
                                {!usersLoading && userRows.length > 0 && (
                                    <div className="border-t border-slate-100 px-3 py-3">
                                        <Pagination
                                            perPageData={pageSize}
                                            total={totalElements}
                                            currentPage={listPage}
                                            changePage={changeListPage}
                                            length={numberOfElements}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                                    <div>
                                        <h2 className="text-base font-bold text-slate-800">Print jobs</h2>
                                        <p className="text-sm text-slate-600">Support lookup by order ID (no browser print).</p>
                                    </div>
                                    <div className="flex flex-wrap items-end gap-2">
                                        <div>
                                            <label className="mb-1 block text-xs font-semibold text-slate-600">
                                                Order ID
                                            </label>
                                            <input
                                                type="number"
                                                className="w-40 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                                                value={orderIdInput}
                                                onChange={(e) => setOrderIdInput(e.target.value)}
                                                placeholder="Order id"
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            title="Load print jobs"
                                            aria-label="Load print jobs"
                                            onClick={() => void loadPrintJobs()}
                                            disabled={jobsLoading}
                                            className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-slate-800 text-white hover:bg-slate-900 disabled:opacity-50"
                                        >
                                            <RefreshCw className={`size-4 ${jobsLoading ? 'animate-spin' : ''}`} />
                                        </button>
                                    </div>
                                </div>
                                <div className="overflow-x-auto rounded-lg border border-slate-100">
                                    <table className="w-full text-sm">
                                        <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-600">
                                            <tr>
                                                <th className="px-3 py-2">Job</th>
                                                <th className="px-3 py-2">Status</th>
                                                <th className="px-3 py-2">Attempts</th>
                                                <th className="px-3 py-2">Created</th>
                                                <th className="px-3 py-2">Printed</th>
                                                <th className="px-3 py-2">Error</th>
                                            </tr>
                                        </thead>
                                        <AnimatedTableBody loading={jobsLoading} itemCount={printJobs.length} animationKey={`pos-jobs-${orderIdInput}`}>
                                            {printJobs.length === 0 ? (
                                                <tr>
                                                    <td colSpan={6} className="px-3 py-8 text-center text-slate-500">
                                                        Enter an order ID and use the refresh button to load jobs.
                                                    </td>
                                                </tr>
                                            ) : (
                                                printJobs.map((j) => (
                                                    <tr key={j.id} className="border-t border-slate-100 bg-white">
                                                        <td className="px-3 py-2">{j.id ?? '—'}</td>
                                                        <td className="px-3 py-2">
                                                            <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-medium">
                                                                {j.status || '—'}
                                                            </span>
                                                        </td>
                                                        <td className="px-3 py-2">{j.attempts ?? '—'}</td>
                                                        <td className="px-3 py-2 text-xs">{formatDt(j.createdAt)}</td>
                                                        <td className="px-3 py-2 text-xs">{formatDt(j.printedAt)}</td>
                                                        <td
                                                            className="max-w-[220px] truncate px-3 py-2 text-xs"
                                                            title={j.errorMessage || ''}
                                                        >
                                                            {j.errorMessage || '—'}
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </AnimatedTableBody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <div className="xl:col-span-1">
                            <div className="sticky top-4 space-y-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                                <div className="flex items-start justify-between gap-2">
                                    <div>
                                        <h2 className="text-base font-bold text-slate-800">Editor</h2>
                                        <p className="mt-1 text-xs text-slate-500">
                                            {activeAccountId != null
                                                ? `Account ${activeAccountId}`
                                                : 'Select Configure on a row'}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        title="Reload settings from server"
                                        aria-label="Reload settings from server"
                                        onClick={() => {
                                            if (activeAccountId != null) void loadSettingsForAccount(activeAccountId);
                                            else toast.error('Select an account first', { autoClose: 2000 });
                                        }}
                                        disabled={editorLoading || activeAccountId == null}
                                        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 disabled:opacity-40"
                                    >
                                        <RefreshCw className={`size-4 ${editorLoading ? 'animate-spin' : ''}`} />
                                    </button>
                                </div>

                                {editorLoading ? (
                                    <div className="flex justify-center py-12">
                                        <div className="size-9 animate-spin rounded-full border-4 border-custom-500 border-l-transparent" />
                                    </div>
                                ) : activeAccountId == null ? (
                                    <p className="py-8 text-center text-sm text-slate-500">
                                        Choose an account from the table to edit POS branding and behavior.
                                    </p>
                                ) : (
                                    <>
                                        <div className="space-y-4">
                                            <label className="flex cursor-pointer items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    checked={!!form.posEnabled}
                                                    onChange={(e) => setForm((f) => ({ ...f, posEnabled: e.target.checked }))}
                                                />
                                                <span className="text-sm font-medium text-slate-800">POS enabled</span>
                                            </label>
                                            <label className="flex cursor-pointer items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    checked={!!form.autoPrintIfSubmit}
                                                    onChange={(e) =>
                                                        setForm((f) => ({ ...f, autoPrintIfSubmit: e.target.checked }))
                                                    }
                                                />
                                                <span className="text-sm font-medium text-slate-800">
                                                    Auto print on submit
                                                </span>
                                            </label>
                                            <div>
                                                <label className="mb-1 block text-xs font-semibold text-slate-600">
                                                    Printer type
                                                </label>
                                                <input
                                                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                                                    value={form.printerType || ''}
                                                    onChange={(e) => setForm((f) => ({ ...f, printerType: e.target.value }))}
                                                />
                                            </div>
                                            <div>
                                                <label className="mb-1 block text-xs font-semibold text-slate-600">
                                                    Paper width
                                                </label>
                                                <select
                                                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                                                    value={form.paperWidth || '58mm'}
                                                    onChange={(e) => setForm((f) => ({ ...f, paperWidth: e.target.value }))}
                                                >
                                                    <option value="58mm">58mm</option>
                                                    <option value="80mm">80mm</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="mb-1 block text-xs font-semibold text-slate-600">
                                                    Store name
                                                </label>
                                                <input
                                                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                                                    value={form.storeName || ''}
                                                    onChange={(e) => setForm((f) => ({ ...f, storeName: e.target.value }))}
                                                />
                                            </div>
                                            <div>
                                                <label className="mb-1 block text-xs font-semibold text-slate-600">
                                                    Store type
                                                </label>
                                                <select
                                                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                                                    value={form.storeType || ''}
                                                    onChange={(e) => setForm((f) => ({ ...f, storeType: e.target.value }))}
                                                >
                                                    <option value="">— Select —</option>
                                                    <option value="PHARMACY">PHARMACY</option>
                                                    <option value="RETAIL">RETAIL</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="mb-1 block text-xs font-semibold text-slate-600">
                                                    Header logo URL
                                                </label>
                                                <input
                                                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                                                    value={form.headerLogoUrl || ''}
                                                    onChange={(e) =>
                                                        setForm((f) => ({ ...f, headerLogoUrl: e.target.value }))
                                                    }
                                                    placeholder="Optional — empty uses default logo"
                                                />
                                                <p className="mt-1 text-xs text-slate-500">
                                                    Leave empty to use the default Antar logo on receipts (preview below).
                                                </p>
                                                <div className="mt-3 flex justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50 p-4">
                                                    <img
                                                        src={logoPreviewSrc}
                                                        alt="Logo preview"
                                                        className="max-h-16 max-w-[140px] object-contain"
                                                        onError={(e) => {
                                                            (e.target as HTMLImageElement).src = defaultPosLogo;
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="mb-1 block text-xs font-semibold text-slate-600">
                                                    Footer text
                                                </label>
                                                <textarea
                                                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                                                    rows={2}
                                                    value={form.footerText || ''}
                                                    onChange={(e) => setForm((f) => ({ ...f, footerText: e.target.value }))}
                                                />
                                            </div>
                                        </div>

                                        <div className="mt-4 rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
                                            <div className="font-semibold text-slate-700">Audit</div>
                                            <div className="mt-2 grid gap-1">
                                                <div>Created: {formatDt(audit.createdAt)}</div>
                                                <div>Updated: {formatDt(audit.updatedAt)}</div>
                                                <div>By: {audit.updatedBy || audit.createdBy || '—'}</div>
                                            </div>
                                        </div>

                                        <ButtonBase
                                            innerText={saveLoading ? 'Saving…' : 'Save changes'}
                                            onClick={() => void saveSettings()}
                                            disabled={saveLoading}
                                            className="mt-4 w-full rounded-lg border-none !bg-[#bd902e] !text-white hover:!bg-[#a67d26]"
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default PosSettings;
